(() => {
  'use strict';

  const existing = window.AUComeceAqui;
  if (existing && typeof existing.init === 'function') {
    existing.init(document);
    return;
  }

  const controllers = new WeakMap();
  const priorityMap = Object.freeze({
    'price-ascending': 'menos-acucar',
    'price-descending': 'mais-proteina',
    relevance: 'mais-gostoso'
  });

  class ComeceAquiController {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-au-comece-form]');
      this.result = root.querySelector('[data-au-comece-result]');
      this.status = root.querySelector('[data-au-comece-status]');
      this.errorTemplate = root.querySelector('[data-au-comece-error-template]');
      this.lifecycle = new AbortController();
      this.requestController = null;
      this.sequence = 0;

      if (!this.form || !this.result || !this.status) return;
      this.enhance();
    }

    enhance() {
      this.root.dataset.enhanced = 'true';
      this.form.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"]')) return;
        this.update(false);
      }, { signal: this.lifecycle.signal });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.update(true);
      }, { signal: this.lifecycle.signal });

      this.root.addEventListener('click', (event) => {
        const retry = event.target.closest('[data-au-comece-retry]');
        if (retry) {
          event.preventDefault();
          this.update(true);
          return;
        }

        const change = event.target.closest('[data-au-comece-change]');
        if (!change) return;
        event.preventDefault();
        const firstChoice = this.form.querySelector('input[type="radio"]');
        firstChoice?.focus({ preventScroll: true });
        firstChoice?.scrollIntoView({ behavior: 'auto', block: 'center' });
        this.announce(this.root.dataset.statusChange);
      }, { signal: this.lifecycle.signal });
    }

    requestUrl() {
      const url = new URL(this.form.action, window.location.origin);
      url.search = new URLSearchParams(new FormData(this.form)).toString();
      return url;
    }

    announce(message) {
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }

    markPending() {
      this.result.setAttribute('aria-busy', 'true');
      this.result.setAttribute('aria-hidden', 'true');
      this.result.setAttribute('data-pending', 'true');
      this.result.inert = true;
      this.announce(this.root.dataset.statusLoading);
    }

    clearPending() {
      this.result.removeAttribute('aria-busy');
      this.result.removeAttribute('aria-hidden');
      this.result.removeAttribute('data-pending');
      this.result.inert = false;
    }

    showError() {
      if (!this.errorTemplate) return;
      this.result.replaceChildren(this.errorTemplate.content.cloneNode(true));
      this.result.dataset.auComeceResultCount = '0';
      this.result.dataset.auComeceError = 'true';
    }

    async update(shouldScroll) {
      this.requestController?.abort();
      const requestController = new AbortController();
      this.requestController = requestController;
      const requestSequence = ++this.sequence;
      const url = this.requestUrl();
      this.markPending();

      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
          signal: requestController.signal
        });
        if (!response.ok) throw new Error(`comece_http_${response.status}`);

        const html = await response.text();
        if (requestSequence !== this.sequence) return;

        const documentResult = new DOMParser().parseFromString(html, 'text/html');
        const nextResult = documentResult.querySelector('[data-au-comece-result]');
        if (!nextResult) throw new Error('comece_result_missing');

        const importedResult = document.importNode(nextResult, true);
        this.result.replaceWith(importedResult);
        this.result = importedResult;
        window.history.replaceState({}, '', url.toString());

        const resultCount = Number.parseInt(this.result.dataset.auComeceResultCount || '0', 10);
        this.announce(this.result.dataset.auComeceResultStatus || this.root.dataset.statusUnavailable);

        const selectedOccasion = this.form.querySelector('input[name="q"]:checked')?.value || '';
        const selectedSort = this.form.querySelector('input[name="sort_by"]:checked')?.value || '';
        document.dispatchEvent(new CustomEvent('australia:comece_aqui_change', {
          detail: {
            occasion: selectedOccasion,
            priority: priorityMap[selectedSort] || '',
            result_count: resultCount,
            source: 'comece-aqui'
          }
        }));

        if (shouldScroll) {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.result.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (requestSequence !== this.sequence) return;
        this.showError();
        this.announce(this.root.dataset.statusError);
      } finally {
        if (requestSequence === this.sequence) this.clearPending();
      }
    }

    destroy() {
      this.requestController?.abort();
      this.lifecycle.abort();
      delete this.root.dataset.enhanced;
      controllers.delete(this.root);
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-au-comece]')) roots.push(scope);
    scope.querySelectorAll?.('[data-au-comece]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new ComeceAquiController(root));
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope?.matches?.('[data-au-comece]')) roots.push(scope);
    scope?.querySelectorAll?.('[data-au-comece]').forEach((root) => roots.push(root));
    roots.forEach((root) => controllers.get(root)?.destroy());
  };

  window.AUComeceAqui = Object.freeze({ init, destroy });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
